import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { ProtectedRoute } from './components/ProtectedRoute';
import { 
  LandingPage,
  LoginPage,
  RegisterPage,
  SuperAdminDashboard,
  ClinicsPage,
  DoctorsPage,
  DoctorDetailPage,
  MothersPage,
  MotherDetailPage,
  SuperAdminSettingsPage,
  SuperAdminCreditsPage,
  CreditAdminCreditsPage,
  CreditAdminDoctorsPage,
  SuperAdminUsersPage,
  SuperAdminAuditLogPage,
  DoctorDashboard, 
  PatientsPage, 
  PatientDetailPage,
  StudioPage,
  BiometrikPage,
  LiveEditorPage,
  ParametricEditorPage,
  AIFacePage,
  Ultrasound4KPage,
  Simulation4DPage,
} from './pages';
import { AboutPage } from './pages/AboutPage';
import { ContactPage } from './pages/ContactPage';

// Hasta Uygulamasi Sayfalari
import PatientLandingPage from './pages/patient/PatientLandingPage';
import PatientDashboard from './pages/patient/PatientDashboard';
import PatientVerifyPage from './pages/patient/PatientVerifyPage';
import UserAgreementPage from './pages/patient/UserAgreementPage';
import PrivacyPolicyPage from './pages/patient/PrivacyPolicyPage';
import PatientAboutPage from './pages/patient/PatientAboutPage';
import PatientContactPage from './pages/patient/PatientContactPage';
import AR360ViewPage from './pages/patient/AR360ViewPage';
import DoctorSettingsPage from './pages/doctor/DoctorSettingsPage';
// ModuleGuard kept (backend always returns has_package=false → treated as "all enabled")
import ModuleGuard from './components/ModuleGuard';

// B2C Mom app
import { MomAuthProvider, useMomAuth } from './context/MomAuthContext';
import MomRegisterPage from './pages/mom/RegisterPage';
import MomLoginPage from './pages/mom/LoginPage';
import MomOnboardingPage from './pages/mom/OnboardingPage';
import MomDashboardPage from './pages/mom/DashboardPage';
import MomAIFacePage from './pages/mom/AIFacePage';
import MomUploadPage from './pages/mom/UploadPage';
import MomVideoPage from './pages/mom/VideoPage';
import KvkkPage from './pages/KvkkPage';

import { EncountersListPage, NewReviewPage, EncounterDetailPage } from './pages/doctor/womens-health';

import { Toaster } from './components/ui/sonner';
import './App.css';

function App() {
  return (
    <AuthProvider>
      <MomAuthProvider>
      <BrowserRouter>
        <Routes>
          {/* Public Routes */}
          <Route path="/" element={<LandingPage />} />
          <Route path="/about" element={<AboutPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/kvkk" element={<KvkkPage />} />

          {/* B2C Mom app */}
          <Route path="/mom" element={<Navigate to="/mom/login" replace />} />
          <Route path="/mom/register" element={<MomRegisterPage />} />
          <Route path="/mom/login" element={<MomLoginPage />} />
          <Route path="/mom/onboarding" element={<MomGuard><MomOnboardingPage /></MomGuard>} />
          <Route path="/mom/dashboard" element={<MomGuard><MomDashboardPage /></MomGuard>} />
          <Route path="/mom/ai-face" element={<MomGuard><MomAIFacePage /></MomGuard>} />
          <Route path="/mom/upload" element={<MomGuard><MomUploadPage /></MomGuard>} />
          <Route path="/mom/video" element={<MomGuard><MomVideoPage /></MomGuard>} />
          
          {/* Super Admin Routes */}
          <Route
            path="/superadmin/dashboard"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/clinics"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <ClinicsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/settings"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminSettingsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/doctors"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DoctorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/doctors/:doctorId"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <DoctorDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/mothers"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <MothersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/mothers/:motherId"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <MotherDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/credits"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminCreditsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/users"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminUsersPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/audit-log"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminAuditLogPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/credit-admin/credits"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'credit_admin']}>
                <CreditAdminCreditsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/credit-admin/doctors"
            element={
              <ProtectedRoute allowedRoles={['super_admin', 'credit_admin']}>
                <CreditAdminDoctorsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/superadmin/*"
            element={
              <ProtectedRoute allowedRoles={['super_admin']}>
                <SuperAdminDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Doctor Routes */}
          {/* Quick Exam moved into PatientDetailPage */}
          <Route
            path="/doctor/dashboard"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <DoctorDashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/patients"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <PatientsPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/patients/:patientId"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <PatientDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/studio"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <ModuleGuard><StudioPage /></ModuleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/biometrik"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <ModuleGuard><BiometrikPage /></ModuleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/editor"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <ModuleGuard><LiveEditorPage /></ModuleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/parametric"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <ModuleGuard><ParametricEditorPage /></ModuleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/ai-face"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <ModuleGuard><AIFacePage /></ModuleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/ultrasound-4k"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <ModuleGuard><Ultrasound4KPage /></ModuleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/module-disabled"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <Navigate to="/doctor/dashboard" replace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/settings"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <DoctorSettingsPage />
              </ProtectedRoute>
            }
          />

          {/* Women's Health Routes */}
          <Route
            path="/doctor/womens-health"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <ModuleGuard><EncountersListPage /></ModuleGuard>
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/womens-health/new"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <NewReviewPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/womens-health/:encounterId"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <EncounterDetailPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/4d-simulation"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <Simulation4DPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/doctor/*"
            element={
              <ProtectedRoute allowedRoles={['doctor']}>
                <DoctorDashboard />
              </ProtectedRoute>
            }
          />
          
          {/* Clinic Admin Routes (placeholder) */}
          <Route
            path="/clinic/*"
            element={
              <ProtectedRoute allowedRoles={['clinic_admin']}>
                <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC]">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-slate-800">Klinik Yönetici Paneli</h1>
                    <p className="text-slate-500 mt-2">Yakında...</p>
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          
          {/* Employee Routes (placeholder) */}
          <Route
            path="/employee/*"
            element={
              <ProtectedRoute allowedRoles={['employee']}>
                <div className="min-h-screen flex items-center justify-center bg-[#F8F7FC]">
                  <div className="text-center">
                    <h1 className="text-2xl font-bold text-slate-800">Çalışan Paneli</h1>
                    <p className="text-slate-500 mt-2">Yakında...</p>
                  </div>
                </div>
              </ProtectedRoute>
            }
          />
          
          {/* =============== HASTA UYGULAMASI ROUTE'LARI =============== */}
          {/* Bu route'lar doktor sisteminden TAMAMEN AYRI */}
          <Route path="/patient" element={<PatientLandingPage />} />
          <Route path="/patient/verify" element={<PatientVerifyPage />} />
          <Route path="/patient/dashboard" element={<PatientDashboard />} />
          <Route path="/patient/sozlesme" element={<UserAgreementPage />} />
          <Route path="/patient/gizlilik" element={<PrivacyPolicyPage />} />
          <Route path="/patient/hakkimizda" element={<PatientAboutPage />} />
          <Route path="/patient/iletisim" element={<PatientContactPage />} />
          <Route path="/patient/ar-360" element={<AR360ViewPage />} />
          <Route path="/doctor/ar-360" element={<AR360ViewPage />} />
          
          {/* Default Redirect */}
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </BrowserRouter>
      </MomAuthProvider>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

// Route guard for mom-authenticated pages. Redirects to /mom/login if not logged in.
const MomGuard = ({ children }) => {
  const { mom, loading } = useMomAuth();
  if (loading) return null;
  if (!mom) return <Navigate to="/mom/login" replace />;
  return children;
};

export default App;
